import DataTable from "react-data-table-component";
import Layout from "../../../components/Layout";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

function ParaSabhaReportTable() {
  const apiURL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const { id } = location?.state || {};
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]);
  const [samparkEmployees, setSamparkEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [uniqueDates, setUniqueDates] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [isDharmadaChecked, setIsDharmadaChecked] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [attendanceData, setAttendanceData] = useState({});

  const formatDate = (date) => {
    if (!date) return "";
    // Adjust date to local time zone by creating a new Date object
    const localDate = new Date(date);
    // Format the date in yyyy-mm-dd
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, "0");
    const day = String(localDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);

  useEffect(() => {
    const refreshToken = localStorage.getItem("refreshToken");
    const roles = localStorage.getItem("roles");
    if (!refreshToken || roles !== "SuperAdmin") {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    fetchSamparkEmployees();
  }, []);

  useEffect(() => {
    if (Object.keys(attendanceData).length === 0) {
      fetchAttendanceForAll();
    }
  }, [data]);

  useEffect(() => {
    setLoading(false);
    if (formattedStartDate && formattedEndDate) {
      fetchAttendanceData();
    }
  }, [
    formattedStartDate,
    formattedEndDate,
    selectedEmployee,
    isDharmadaChecked,
    searchQuery,
    attendanceData,
  ]);

  const fetchSamparkEmployees = async () => {
    console.log("fetchSamparkEmployees is called");
    const token = localStorage.getItem("token");

    try {
      const response = await axios.get(`${apiURL}/SamparkEmploye/Get`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSamparkEmployees(response.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error fetching employees");
    }
  };

  // Function to fetch attendance data
  const fetchAttendanceData = async () => {
    console.log("fetchAttendanceData is called");

    setLoading(true);
    try {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${apiURL}/UserAttendence/GetUserByDate?fromDate=${formattedStartDate}&toDate=${formattedEndDate}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      };

      const response = await axios.request(config);
      let apiData = response.data.data;

      if (selectedEmployee !== "all") {
        apiData = apiData.filter((item) =>
          item.userMaster.groupDetails.some(
            (group) => group.samparkEmployeId === Number(selectedEmployee)
          )
        );
      }

      // Apply filtering based on checkbox state
      if (isDharmadaChecked) {
        apiData = apiData.filter((item) => item.userMaster.dharmadaId);
      }

      // Filter data based on search query
      if (searchQuery) {
        apiData = apiData.filter((item) => {
          const fullName =
            `${item.userMaster.firstName} ${item.userMaster.lastName} ${item.userMaster.surName}`.toLowerCase();
          const contactNo = item.userMaster.contactNo?.toLowerCase();
          const houseNo = item.userMaster.houseNo?.toLowerCase();
          const street = item.userMaster.street?.toLowerCase();
          const address = item.userMaster.address?.toLowerCase();
          const attendanceId = String(item.userMaster.sequenceNo).toLowerCase();

          return (
            fullName.includes(searchQuery) ||
            houseNo?.includes(searchQuery) ||
            street?.includes(searchQuery) ||
            address?.includes(searchQuery) ||
            (searchQuery.length === 10 && contactNo === searchQuery) || // Exact match for full mobile number
            searchQuery === attendanceId // Exact match for attendance ID
          );
        });
      }

      apiData.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Extract unique dates
      const uniqueDates = [
        ...new Set(apiData.map((item) => item.date.slice(0, 10))),
      ].sort((a, b) => new Date(a) - new Date(b));

      // Group data by 'haribhaktId'
      const groupedData = apiData.reduce((acc, item) => {
        if (!acc[item.haribhaktId]) acc[item.haribhaktId] = [];
        if (
          !acc[item.haribhaktId].some(
            (existing) => existing.date.slice(0, 10) === item.date.slice(0, 10)
          )
        ) {
          acc[item.haribhaktId].push(item);
        }
        return acc;
      }, {});

      const dynamicColumns = uniqueDates.map((date) => ({
        name: `${date}`,
        selector: (row) => {
          const attendance = groupedData[row.haribhaktId]?.find(
            (item) => item.date.slice(0, 10) === date
          );
          return attendance
            ? attendance.attendenceStatus
              ? "P" // Present
              : "A" // Absent
            : "-";
        },
        sortable: true,
        width: "150px",
        center: "true",
        cell: (row) => {
          const attendance = groupedData[row.haribhaktId]?.find(
            (item) => item.date.slice(0, 10) === date
          );
          const status = attendance
            ? attendance.attendenceStatus
              ? "P"
              : "A"
            : "-";

          let color = "";
          if (status === "P") {
            color = "green";
          } else if (status === "A") {
            color = "red";
          }

          return (
            <div style={{ color, fontSize: "16px", fontWeight: "700" }}>
              {status}
            </div>
          );
        },
      }));

      // Calculate the "Total" column
      const dataWithTotal = Object.keys(groupedData).map((key) => {
        const row = groupedData[key][0];
        let presentCount = 0;
        let absentCount = 0;

        uniqueDates.forEach((date) => {
          const attendance = groupedData[key].find(
            (item) => item.date.slice(0, 10) === date
          );
          if (attendance)
            attendance.attendenceStatus ? presentCount++ : absentCount++;
        });

        return {
          ...row,
          total: (
            <>
              <span className="text-success px-1">
                <b>{presentCount}</b>
              </span>
              /
              <span className="text-danger px-1">
                <b>{absentCount}</b>
              </span>
            </>
          ),
        };
      });

      // Update columns and data state
      setColumns([
        ...staticColumns,
        ...dynamicColumns,
        {
          name: "Total",
          selector: (row) => row.total,
          sortable: true,
          center: "true",
        },
      ]);
      setData(dataWithTotal);
      setUniqueDates(uniqueDates);
      setGroupedData(groupedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceForAll = async () => {
    console.log("fetchAttendanceForAll is called");

    let attendanceSummary = {};

    for (let haribhakt of data) {
      let config = {
        method: "get",
        url: `${apiURL}/UserAttendence/GetByList?haribhaktId=${haribhakt.userMaster.id}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      };

      try {
        const response = await axios.request(config);
        if (response.data.status) {
          const records = response.data.data;
          let uniqueDates = {};
          let present = 0;

          records.forEach((record) => {
            if (!uniqueDates[record.date]) {
              uniqueDates[record.date] = record.attendenceStatus;
            }
          });

          let totalRecords = Object.keys(uniqueDates).length;
          Object.values(uniqueDates).forEach((status) => {
            if (status) present++;
          });

          attendanceSummary[haribhakt.userMaster.id] = {
            totalRecords,
            present,
          };
        }
      } catch (error) {
        console.error(
          `Error fetching attendance for Haribhakt ${haribhakt.id}:`,
          error
        );
      }
    }

    setAttendanceData(attendanceSummary);
  };

  // console.log(`attendanceData: ${JSON.stringify(attendanceData)}`);

  // Define static columns outside the API function
  const staticColumns = [
    {
      name: "Attendance Id",
      selector: (row) => `${row.userMaster.sequenceNo}`,
      sortable: true,
      width: "156px",
      center: "true",
    },
    {
      name: "Haribhakt Id",
      selector: (row) => row.haribhaktId,
      sortable: true,
      width: "156px",
      center: "true",
    },
    {
      name: "Name(Total Sabha/Total Attendance)",
      selector: (row) => {
        const attendance = attendanceData[row.haribhaktId] || {
          totalRecords: 0,
          present: 0,
        };

        return (
          <div>
            {row.userMaster.firstName} {row.userMaster.lastName}{" "}
            {row.userMaster.surName} &nbsp;
            <span style={{ color: "green", fontWeight: "bold" }}>
              ({attendance.present}
            </span>
            <span
              style={{
                color: "blue",
                fontWeight: "bold",
              }}
            >
              /{attendance.totalRecords})
            </span>
          </div>
        );
      },
      sortable: true,
      width: "400px",
    },
    {
      name: "Mobile Number",
      selector: (row) => `${row.userMaster.contactNo}`,
    },
    {
      name: "House No",
      selector: (row) => row.userMaster.houseNo,
    },
    {
      name: "Street",
      selector: (row) => `${row.userMaster.street}`,
      width: "300px",
    },
    {
      name: "Address",
      selector: (row) => `${row.userMaster.address}`,
      width: "300px",
    },
    {
      name: "Dharmada Id",
      selector: (row) => `${row.userMaster?.dharmadaId || "-"}`,
    },
  ];

  const onDateChange = (start, end) => {
    console.log("Selected Date Range: ", start, " to ", end);
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Attendance");

    const totalCount = Object.keys(groupedData).map((key) => {
      let presentCount = 0;
      let absentCount = 0;

      uniqueDates.forEach((date) => {
        const attendance = groupedData[key]?.find(
          (item) => item.date.slice(0, 10) === date
        );
        if (attendance) {
          attendance.attendenceStatus ? presentCount++ : absentCount++;
        }
      });

      return { presentCount, absentCount };
    });

    const formattedData = data.map((row, index) => {
      let attendanceData = uniqueDates.reduce((acc, date) => {
        const attendance = groupedData[row.haribhaktId]?.find(
          (item) => item.date.slice(0, 10) === date
        );
        acc[date] = attendance
          ? attendance.attendenceStatus
            ? "P"
            : "A"
          : "-";
        return acc;
      }, {});

      // 🛠 Ensure no undefined/null values
      const rowData = {
        No: index + 1,
        "Haribhakt Id": row.haribhaktId || "-",
        "Attendance Id": row.userMaster?.sequenceNo?.toString() || "-",
        "Full Name": `${row.userMaster?.firstName || ""} ${
          row.userMaster?.lastName || ""
        } ${row.userMaster?.surName || ""}`.trim(),
        "Mobile Number": row.userMaster?.contactNo?.toString() || "-",
        "House No": row.userMaster?.houseNo?.toString() || "-",
        Street: row.userMaster?.street || "-",
        Address: row.userMaster?.address || "-",
        ...attendanceData,
        "Present / Absent": `${totalCount[index]?.presentCount || 0} / ${
          totalCount[index]?.absentCount || 0
        }`,
      };

      // 🛠 Only include "Dharmada Id" if checkbox is checked
      if (isDharmadaChecked) {
        rowData["Dharmada Id"] = row.userMaster?.dharmadaId
          ? row.userMaster.dharmadaId.toString()
          : "-";
      }

      return rowData;
    });

    // 🛠 Define headers dynamically
    let headers = [
      "No",
      "Haribhakt Id",
      "Attendance Id",
      "Full Name",
      "Mobile Number",
      "House No",
      "Street",
      "Address",
      ...uniqueDates,
      "Present / Absent",
    ];

    if (isDharmadaChecked) {
      headers.splice(8, 0, "Dharmada Id"); // Insert "Dharmada Id" at correct position
    }

    worksheet.addRow(headers);

    // 🛠 Add data rows with default values
    formattedData.forEach((row) => {
      const newRow = worksheet.addRow(
        headers.map((header) => row[header] ?? "-") // Ensure all values are defined
      );

      uniqueDates.forEach((date, colIndex) => {
        const cell = newRow.getCell(headers.indexOf(date) + 1);
        if (cell.value === "P") {
          cell.font = { color: { argb: "008000" } };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        } else if (cell.value === "A") {
          cell.font = { color: { argb: "FF0000" } };
          cell.alignment = { horizontal: "center", vertical: "middle" };
        }
      });

      // 🛠 Fix "Present / Absent" cell error
      const presentAbsentCell = newRow.getCell(headers.length);
      if (typeof presentAbsentCell.value === "string") {
        const presentAbsentValue = presentAbsentCell.value.split(" / ");
        presentAbsentCell.value = {
          richText: [
            {
              text: presentAbsentValue[0],
              font: { color: { argb: "008000" } },
            }, // Green for present
            { text: " / " },
            {
              text: presentAbsentValue[1],
              font: { color: { argb: "FF0000" } },
            }, // Red for absent
          ],
        };
      }
    });

    // 🛠 Set column widths dynamically
    const columnWidths = [
      5,
      10,
      10,
      25,
      15,
      10,
      25,
      30,
      ...(isDharmadaChecked ? [15] : []), // Add width for "Dharmada Id" if needed
      ...uniqueDates.map(() => 12),
      15,
    ];

    worksheet.columns = columnWidths.map((width) => ({ width }));

    // Generate Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `AttendanceData_from_${formattedStartDate}_to_${formattedEndDate}.xlsx`
    );
  };

  const generatePDF = () => {
    const RECORDS_PER_PAGE = 12; // Max records per page
    const MAX_DYNAMIC_COLUMNS = 8; // Max dynamic columns per page

    const totalCount = Object.keys(groupedData).map((key) => {
      let presentCount = 0;
      let absentCount = 0;

      uniqueDates.forEach((date) => {
        const attendance = groupedData[key]?.find(
          (item) => item.date.slice(0, 10) === date
        );
        if (attendance) {
          attendance.attendenceStatus ? presentCount++ : absentCount++;
        }
      });

      return { presentCount, absentCount };
    });

    const doc = new jsPDF({ orientation: "landscape", format: "a4" });

    // Set font size for the header
    doc.setFontSize(14);
    const pageWidth = doc.internal.pageSize.width;

    // Define header text
    const firstLine = `Attendance Report (From: ${formattedStartDate} To: ${formattedEndDate})`;

    // Function to add the header text
    const addHeader = () => {
      const firstLineWidth = doc.getTextWidth(firstLine);
      doc.text(firstLine, (pageWidth - firstLineWidth) / 2, 10);
    };

    // Function to truncate long addresses
    const truncateAddress = (address) => {
      if (!address) return "-";
      const MAX_CHARS = 3 * 20;
      return address.length > MAX_CHARS
        ? address.substring(0, MAX_CHARS - 3) + "..."
        : address;
    };

    // Split dynamic columns into chunks of 8
    const chunkedDates = [];
    for (let i = 0; i < uniqueDates.length; i += MAX_DYNAMIC_COLUMNS) {
      chunkedDates.push(uniqueDates.slice(i, i + MAX_DYNAMIC_COLUMNS));
    }

    // Static headers (without P/A column initially)
    let staticHeaders = [
      "No",
      "Haribhakt Id",
      "Attendance Id",
      "Full Name",
      "Mobile Number",
      "House No",
      "Street",
      "Address",
    ];

    // Add "Dharmada Id" only if checkbox is checked
    if (isDharmadaChecked) {
      staticHeaders.push("Dharmada Id");
    }

    // Process records in batches of 12
    for (
      let recordStart = 0;
      recordStart < data.length;
      recordStart += RECORDS_PER_PAGE
    ) {
      const batchRecords = data.slice(
        recordStart,
        recordStart + RECORDS_PER_PAGE
      );

      chunkedDates.forEach((dateChunk, pageIndex) => {
        if (!(recordStart === 0 && pageIndex === 0)) doc.addPage(); // Add a new page except for the first one

        // Headers for current page
        const currentHeaders = [staticHeaders.concat(dateChunk, ["P / A"])];

        // Data for current page
        const currentRows = batchRecords.map((record, index) => {
          let rowData = [
            recordStart + index + 1, // Row number
            record.haribhaktId || "-",
            record.userMaster?.sequenceNo?.toString() || "-",
            `${record.userMaster?.firstName || ""} ${
              record.userMaster?.lastName || ""
            } ${record.userMaster?.surName || ""}`.trim(),
            record.userMaster?.contactNo?.toString() || "-",
            record.userMaster?.houseNo?.toString() || "-",
            record.userMaster?.street || "-",
            truncateAddress(record.userMaster?.address),
          ];

          // Add "Dharmada Id" if selected
          if (isDharmadaChecked) {
            rowData.push(
              record.userMaster?.dharmadaId
                ? record.userMaster.dharmadaId.toString()
                : "-"
            );
          }

          // Add dynamic attendance data
          rowData.push(
            ...dateChunk.map((date) => {
              const attendance = groupedData[record.haribhaktId]?.find(
                (item) => item.date.slice(0, 10) === date
              );
              return attendance
                ? attendance.attendenceStatus
                  ? "P"
                  : "A"
                : "-";
            })
          );

          // Add P/A column **at the last**
          rowData.push(
            `${totalCount[recordStart + index]?.presentCount || 0} / ${
              totalCount[recordStart + index]?.absentCount || 0
            }`
          );

          return rowData;
        });

        // Define column widths dynamically
        let columnWidths = [
          8,
          12,
          12,
          35,
          20,
          12,
          25,
          30,
          ...(isDharmadaChecked ? [15] : []),
          ...dateChunk.map(() => 12),
          20,
        ];

        // Add header and table
        addHeader();
        autoTable(doc, {
          head: currentHeaders,
          body: currentRows,
          startY: 15,
          theme: "grid",
          columnStyles: columnWidths.reduce((acc, width, index) => {
            acc[index] = { cellWidth: width };
            return acc;
          }, {}),
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontSize: 10,
            lineColor: [0, 0, 0],
            lineWidth: 0.5,
          },
          bodyStyles: { lineColor: [0, 0, 0], lineWidth: 0.2, fontSize: 8 },
          margin: { top: 20, left: 10, right: 10, bottom: 5 },
          didDrawPage: addHeader,
        });
      });
    }

    // Save the generated PDF
    doc.save(
      `Attendance_Report_${formattedStartDate}_to_${formattedEndDate}.pdf`
    );
  };

  return (
    <Layout ac5="active" id={id}>
      <section className="content-header pb-0">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h5>Parasabha Report Details</h5>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/manage">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Parasabha Report Details</li>
              </ol>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  {/* First Line: Search Bar */}
                  <div className="mb-3">
                    <input
                      className="form-control"
                      type="search"
                      placeholder="Search"
                      title="Search within table"
                      value={searchQuery}
                      onChange={(e) =>
                        setSearchQuery(e.target.value.toLowerCase())
                      }
                    />
                  </div>

                  {/* Second Line: Checkbox, Select, and Date Range */}
                  <div className="d-flex flex-column flex-md-row flex-wrap justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <input
                        className="mr-2"
                        type="checkbox"
                        id="dharmadaId"
                        checked={isDharmadaChecked}
                        onChange={(e) => setIsDharmadaChecked(e.target.checked)}
                      />
                      <label htmlFor="dharmadaId" className="mb-0">
                        Dharmada Id
                      </label>
                    </div>

                    <div className="d-flex align-items-center mb-2">
                      <label className="mr-2">Sampark</label>
                      <select
                        className="form-control"
                        onChange={(e) => setSelectedEmployee(e.target.value)}
                        value={selectedEmployee}
                      >
                        <option value="all">All</option>
                        {samparkEmployees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            ({emp.id}) {emp.firstName} {emp.lastName}{" "}
                            {emp.surName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="d-flex align-items-center mb-2">
                      <label className="mr-2 text-nowrap">Date Range:</label>
                      <DatePicker
                        selectsRange={true}
                        startDate={startDate}
                        endDate={endDate}
                        placeholderText="mm/dd/yyyy-mm/dd/yyyy"
                        onChange={(update) => {
                          setDateRange(update);
                          if (update[0] && update[1]) {
                            onDateChange(update[0], update[1]);
                          }
                        }}
                        isClearable={true}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Third Line: Buttons */}
                  <div className="d-flex justify-content-center">
                    <button
                      onClick={exportToExcel}
                      className="btn btn-primary mr-2"
                    >
                      <i className="fas fa-file-export"></i> Excel
                    </button>
                    <button onClick={generatePDF} className="btn btn-primary">
                      <i className="fas fa-file-export"></i> PDF
                    </button>
                  </div>
                </div>

                <div className="card-body text-center">
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm "
                        role="status"
                        aria-hidden="true"
                      ></span>
                    </>
                  ) : (
                    <DataTable
                      columns={columns}
                      data={data}
                      pagination
                      responsive
                      className="custom-table"
                      onChangePage={(page) => setCurrentPage(page)}
                      onChangeRowsPerPage={(newPerPage) => {
                        setRowsPerPage(newPerPage);
                        setCurrentPage(1);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ToastContainer position="top-center" style={{ width: "auto" }} />
    </Layout>
  );
}

export default ParaSabhaReportTable;
